import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Typography } from 'antd';
import { FileAddOutlined } from '@ant-design/icons';
import { useQueries } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Roles } from '../../Types/interfaces';
import type {
  OrganizationRegistration,
  ReducedOrganization,
} from '../../Types/interfaces';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useHasRole } from '../../hooks/useHasRole';
import { useUserOrganizations } from '../../hooks/useOrganizations';
import { useCurrentStudyYear } from '../../hooks/useStudyYear';
import { useDashboardNavigate } from '../../hooks/useDashboardNavigate';
import { GetOrganizationRegistrationsByOrganizationId } from '../../helper/apiHelper';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { formatDate } from '../../Utils/date';

// the page with the new study year agreement form (OrganizationListComponent)
const REGISTERED_ORGANIZATIONS_PATH =
  'organization-representative/registered-oraganizations';

const SEEN_STORAGE_KEY = 'pendingAcordReminderSeen';

// sessionStorage can be unavailable (e.g. blocked site data): then the reminder
// is shown once per page load instead of once per login
const wasSeenThisSession = (userId: number): boolean => {
  try {
    return sessionStorage.getItem(SEEN_STORAGE_KEY) === String(userId);
  } catch {
    return false;
  }
};

const markSeenThisSession = (userId: number): void => {
  try {
    sessionStorage.setItem(SEEN_STORAGE_KEY, String(userId));
  } catch {
    // ignored, see wasSeenThisSession
  }
};

// called on login so the reminder comes back while the agreement is still pending
export const resetPendingAcordReminder = (): void => {
  try {
    sessionStorage.removeItem(SEEN_STORAGE_KEY);
  } catch {
    // ignored, see wasSeenThisSession
  }
};

// undefined until the registrations of the organization are loaded
const registrationsOf = (
  results: UseQueryResult<OrganizationRegistration[]>[]
): (OrganizationRegistration[] | undefined)[] =>
  results.map((result) => result.data);

// Partner organizations of the representative without a registration in the
// current study year, the same condition that shows the new study year form
// in OrganizationRegistrationProcessComponent
const usePendingAcordOrganizations = (
  enabled: boolean
): ReducedOrganization[] => {
  const loggedInUser = useCurrentUser();
  const { data: userOrganizations } = useUserOrganizations(
    enabled ? loggedInUser.id : undefined
  );
  const { data: currentStudyYear } = useCurrentStudyYear();

  const partnerOrganizations = useMemo(
    () =>
      (userOrganizations ?? []).filter(
        (organization) => !organization.isUniversityOrganization
      ),
    [userOrganizations]
  );

  const registrationsOfOrganizations = useQueries({
    queries: partnerOrganizations.map((organization) => ({
      queryKey: [...QUERY_KEYS.ORGANIZATION_REGISTRATIONS, organization.id],
      queryFn: () =>
        GetOrganizationRegistrationsByOrganizationId(String(organization.id)),
      enabled: enabled && !!currentStudyYear,
    })),
    combine: registrationsOf,
  });

  return useMemo(() => {
    if (!enabled || !currentStudyYear) {
      return [];
    }

    return partnerOrganizations.filter((_, index) => {
      const registrations = registrationsOfOrganizations[index];
      return (
        registrations !== undefined &&
        !registrations.some(
          (registration) =>
            registration.collaborationDocument.studyYearId ===
            currentStudyYear.id
        )
      );
    });
  }, [
    enabled,
    currentStudyYear,
    partnerOrganizations,
    registrationsOfOrganizations,
  ]);
};

export const PendingAcordReminder = (): React.ReactElement | null => {
  const { t } = useTranslation();
  const loggedInUser = useCurrentUser();
  const hasRole = useHasRole();
  const navigateTo = useDashboardNavigate();
  const { data: currentStudyYear } = useCurrentStudyYear();
  // kept after opening, the pending list stops loading once the reminder was seen
  const [shownOrganizations, setShownOrganizations] = useState<
    ReducedOrganization[] | null
  >(null);
  const [open, setOpen] = useState(true);

  const isRepresentative =
    loggedInUser.id !== 0 && hasRole(Roles.OrganizationRepresentative);
  const pendingOrganizations = usePendingAcordOrganizations(
    isRepresentative && !wasSeenThisSession(loggedInUser.id)
  );

  if (shownOrganizations === null && pendingOrganizations.length > 0) {
    setShownOrganizations(pendingOrganizations);
  }

  useEffect(() => {
    if (shownOrganizations !== null) {
      markSeenThisSession(loggedInUser.id);
    }
  }, [shownOrganizations, loggedInUser.id]);

  if (shownOrganizations === null) {
    return null;
  }

  const handleFillIn = (): void => {
    setOpen(false);
    navigateTo(REGISTERED_ORGANIZATIONS_PATH);
  };

  const studyYearRange = {
    start: formatDate(currentStudyYear?.startDate),
    end: formatDate(currentStudyYear?.endDate),
  };

  return (
    <Modal
      open={open}
      title={t('pendingAcordReminder.title')}
      onCancel={() => setOpen(false)}
      footer={[
        <Button key="later" onClick={() => setOpen(false)}>
          {t('pendingAcordReminder.later')}
        </Button>,
        <Button
          key="fillIn"
          type="primary"
          icon={<FileAddOutlined />}
          onClick={handleFillIn}
        >
          {t('pendingAcordReminder.fillIn')}
        </Button>,
      ]}
    >
      {shownOrganizations.length === 1 ? (
        <Typography.Paragraph>
          {t('pendingAcordReminder.descriptionSingle', {
            ...studyYearRange,
            name: shownOrganizations[0].name,
          })}
        </Typography.Paragraph>
      ) : (
        <>
          <Typography.Paragraph>
            {t('pendingAcordReminder.descriptionMultiple', studyYearRange)}
          </Typography.Paragraph>
          <ul>
            {shownOrganizations.map((organization) => (
              <li key={organization.id}>
                <Typography.Text strong>{organization.name}</Typography.Text>
              </li>
            ))}
          </ul>
        </>
      )}
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        {t('pendingAcordReminder.hint')}
      </Typography.Paragraph>
    </Modal>
  );
};
